/* global window */
import React, { useState, useEffect } from 'react';
import { FeaturePanel, Section, SectionHeader } from '../ui';
import { employerQA, agencyQA } from './features.data';
import { UseCaseCarousel } from './use-case-carousel';

type FeatureTab = 'employers' | 'agencies';

// eslint-disable-next-line import/prefer-default-export
export function Features() {
  const [activeTab, setActiveTab] = useState<FeatureTab>('employers');

  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash === '#agencies') setActiveTab('agencies');
      else if (hash === '#employers') setActiveTab('employers');
    };
    handleHashChange();
    const handleForceSwitch = (e: CustomEvent) => {
      setActiveTab(e.detail);
    };
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('force-tab', handleForceSwitch as EventListener);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('force-tab', handleForceSwitch as EventListener);
    };
  }, []);

  const employerFeatures = [
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_free_post.webp"
          alt="Free job posting"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Post jobs for free — pay only if you hire',
      description: 'Zero cost to list your open positions.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_workflow.webp"
          alt="Structured workflow"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Multiple agencies, one structured workflow',
      description:
        'Streamline a wide network of licensed agencies into a single, manageable pipeline.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_transparent_fee.webp"
          alt="Transparent fees"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Transparent & efficient fee model',
      description: 'Only pay the service fee you set in your job posting. No hidden costs.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_metrics.webp"
          alt="Performance metrics"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Measurable performance metrics',
      description:
        'Track success rates and time-to-fill across all partners. You can always work with only the most effective agencies of your choice.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_faster_hiring.webp"
          alt="Faster hiring"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Faster time-to-hire',
      description:
        'Fill your roles quickly by accessing a wider network of committed agencies instantly.',
    },
  ];

  const agencyFeatures = [
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_verified.webp"
          alt="Verified jobs"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Verified employer job orders',
      description:
        'Only real, active roles from companies interested in engaging with external recruiters.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_submission.webp"
          alt="Submission management"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Structured submission management',
      description:
        'Reduce data entry — get organized, user-friendly features only for tracking all your submittals.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_feedback.webp"
          alt="Fast communication"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Transparent & fast communication',
      description:
        'No more follow-up emails — we notify you when your candidate progresses with timely feedback.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_visibility.webp"
          alt="Performance visibility"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Performance-based visibility',
      description: 'Top-performing agencies get more exposure to employers.',
    },
    {
      icon: (
        <img
          src="/assets/icons/icon_feat_subscription.webp"
          alt="Subscription access"
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      ),
      title: 'Subscription-based access',
      description: 'Predictable cost with full access to our open marketplace platform.',
    },
  ];

  return (
    <Section variant="alternate" className="relative overflow-hidden">
      <div id="employers" className="absolute -top-4" />
      <div id="agencies" className="absolute -top-4" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <SectionHeader title="Here are some of our game-changing features" />
        <div
          className="inline-flex bg-white p-1 rounded-full border border-slate-200 shadow-sm mb-12"
          role="tablist"
          aria-label="Features for Employers and Agencies"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'employers'}
            aria-controls="employers-panel"
            id="employers-tab"
            onClick={() => setActiveTab('employers')}
            className={`px-8 py-4 rounded-full text-lg font-bold transition duration-200 ${
              activeTab === 'employers'
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            For Employers
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'agencies'}
            aria-controls="agencies-panel"
            id="agencies-tab"
            onClick={() => setActiveTab('agencies')}
            className={`px-8 py-4 rounded-full text-lg font-bold transition duration-200 ${
              activeTab === 'agencies'
                ? 'bg-secondary text-white shadow-md'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            For Agencies
          </button>
        </div>
        <div className="max-w-6xl mx-auto">
          <div key={activeTab} className="animate-panel-fade-up">
            {activeTab === 'employers' ? (
              <>
                <FeaturePanel
                  id="employers-panel"
                  tabId="employers-tab"
                  title="A more effective & cost-efficient way to hire"
                  subtitle="Control the process. Reduce the risk."
                  subtitleColor="tertiary"
                  titleTag="h3"
                  features={employerFeatures}
                  qaItems={employerQA}
                  qaTitle="FAQ"
                />
                <UseCaseCarousel />
              </>
            ) : (
              <FeaturePanel
                id="agencies-panel"
                tabId="agencies-tab"
                title="Endless job opportunities at the tip of your fingers"
                subtitle="Access real demand. Spend less time finding job leads. Compete on quality."
                subtitleColor="tertiary"
                titleTag="h3"
                features={agencyFeatures}
                qaItems={agencyQA}
                qaTitle="FAQ"
              />
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}
